import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
    DossierActionContext,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierProcesses,
    useDossierRoutingContext,
    useDossierCustomHeader,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import DossierFormaliteProcessesFooter from '../DossierFormaliteProcessesFooter/DossierFormaliteProcessesFooter';
import DossierFormaliteProcessesHeader from '../DossierFormaliteProcessesHeader/DossierFormaliteProcessesHeader';
import DossierFormaliteProcessesList from '../DossierFormaliteProcessesList/DossierFormaliteProcessesList';

const DossierFormaliteProcesses: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const [publishProcessing, setPublishProcessing] = useState<boolean>(false);
    const { setHeaderButton, setHeaderTitle, setPathOnExit } = useDossierCustomHeader();
    const navigate = useNavigate();
    const location = useLocation();
    const { triggerAction } = useContext(DossierActionContext);
    const {
        isFormalityComplete,
        publishGreffeValidatorProcess,
        canAddFormality,
        getExternalUrlByFormality,
        canPublish,
        getNotPublishableReason,
        isProcessOfValidator,
        getCurrentProcessOfType,
    } = useDossierProcesses();
    const { getAddFormalityRoute, getDossierValidationRoute } = useDossierRoutingContext();
    const enablePublishBtn = useMemo(
        () => (dossier ? canPublish(dossier) : false),
        [canPublish, dossier],
    );
    const currentProcess = useMemo(
        () => (dossier ? getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE) : undefined),
        [dossier, getCurrentProcessOfType],
    );
    const showPublishBtn = useMemo(
        () =>
            currentProcess !== undefined &&
            isProcessOfValidator(currentProcess, VALIDATOR_DEMAT_FACILE),
        [currentProcess, isProcessOfValidator],
    );
    const publishable = enablePublishBtn && showPublishBtn;

    const navigateToInfoValidation = () => {
        const currentLocation = location.pathname + location.search;

        setHeaderTitle(`Vérifier les informations du dossier ${dossier?.numero}`);
        setHeaderButton('Enregister et quitter');
        setPathOnExit(currentLocation);

        const path = getDossierValidationRoute(dossier?.id as number);
        if (path) {
            navigate(path);
        }
    };

    const navigateToParcours = () => {
        const path = getAddFormalityRoute(dossier?.id as number);

        if (path) {
            navigate(path);
        }
    };

    const navigateToExternalValidation = useCallback((externalUrl: string) => {
        window.open(externalUrl, '_blank');
    }, []);

    const handlePublish = async () => {
        if (!dossier) {
            return;
        }
        if (!publishable) {
            enqueueSnackbar(
                'Ce dossier ne peut pas être publié. Veuillez vérifier si toutes les informations sont remplies. (formaliste, SIREN...etc)',
                {
                    variant: 'error',
                },
            );

            return;
        }

        setPublishProcessing(true);

        try {
            const response = await publishGreffeValidatorProcess(dossier);

            // Open the website where the greffe has been published
            if (response.data?.externalUrl) {
                window.open(response.data.externalUrl as string, '_blank');
            }

            enqueueSnackbar('Votre dossier a bien été publié');
        } catch (err) {
            enqueueSnackbar('Une erreur est survenue lors de la publication de votre dossier', {
                variant: 'error',
            });
            console.error(err);
        } finally {
            setPublishProcessing(true);
            refreshDossier();
        }
    };

    const formalities = useMemo(
        () =>
            dossier?.formalites.map((formalite) => ({
                libelle: formalite.libelle,
                documentsCount: formalite.documentsCount,
                optionsCount: formalite.optionsCount,
                complete: isFormalityComplete(dossier, formalite),
                externalUrl: getExternalUrlByFormality(dossier, formalite),
                onExternalUrlClick: navigateToExternalValidation,
                id: formalite.id,
            })),
        [dossier, isFormalityComplete, getExternalUrlByFormality, navigateToExternalValidation],
    );

    if (!dossier) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }

    return (
        <>
            <DossierFormaliteProcessesHeader
                organizationLabel={dossier.greffeLibelle as string}
                onUpdateOrganization={() => triggerAction('selectGreffe', [dossier])}
            />
            {formalities && (
                <DossierFormaliteProcessesList
                    formalities={formalities}
                    publishable={enablePublishBtn}
                />
            )}
            <DossierFormaliteProcessesFooter
                canAddFormality={canAddFormality(dossier)}
                enablePublishButton={enablePublishBtn}
                showPublishButton={showPublishBtn}
                publishProcessing={publishProcessing}
                onNavigateToValidation={navigateToInfoValidation}
                onPublish={handlePublish}
                onAddFormality={navigateToParcours}
                notPublishableReason={getNotPublishableReason(dossier)}
                onShowProcess={
                    currentProcess?.externalUrl
                        ? () => navigateToExternalValidation(currentProcess?.externalUrl)
                        : undefined
                }
            />
        </>
    );
};

export default DossierFormaliteProcesses;
